import type { RootStackParamList } from './navigation/types';
import type { ProctorAllocation } from '../../shared/native-modules/types/JSTProctorBridge';
import type {
    BridgeImplementations,
    TypedNativeModules,
} from '../../shared/native-modules/types/NativeModulesTypes';
import type {
    JSTAppSessionInfo,
    TareAccountStatus,
    TareAppModel,
    TareDeviceModel,
} from '../../shared/native-modules/types/TareBridge';
import type { NativeContextRNProps } from '../../shared/props/NativeContextRNProps';
import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';

import { AppContent, type AppContentProps } from './AppContent';
import { AppContentGraphQL } from './AppContentGraphQL';
import { CompanySummaryDisclaimerModal } from './components/core/JobComponent/EmployerInsights/CompanySummaryDisclaimerModal';
import { AIDisclaimerModal } from './components/core/JobComponent/JobSummaryTest/AIDisclaimerModal';
import { CommutePreferenceModal } from './components/core/JobComponent/JobSummaryTest/CommuteSection/CommutePreferenceModal';
import { NavigationBar } from './components/core/JobComponent/NavigationBar/NavigationBar';
import { CollectionModal } from './components/core/ProfileInsights/components/common/CollectionModal';
import { CollectionModalHeader } from './components/core/ProfileInsights/components/common/CollectionModal/CollectionModalHeader';
import { JMUIModalContextProvider } from './components/core/ProfileInsights/context/JMUIModalContextProvider';
import { ProfileWebViewScreen } from './components/core/ProfileWebViewScreen';
import { ReportContentModal } from './components/core/ReportContent/ReportContent';
import { ReportContentHeader } from './components/core/ReportContent/ReportContent/ReportContentHeader';
import {
    isDesktopDisplay as isDesktopDisplaySurface,
    type RnViewJobDisplay,
    ViewJobDisplayProvider,
} from './context/ViewJobDisplayContext';
import { getPlacementIdFromUrl } from './hooks/getJobDataInput';
import { getPlatformOptions } from './PlatformOptions';
import { extractLocale } from './util/extractLocale';
import { useGetApolloDefaultOptions } from './util/useGetApolloDefaultOptions';
import { jobCardDataLocalTypePolicies } from '../../jobcard/typePolicies';
import { Box, ChevronLeft, Pressable, Modal } from '../../mads';
import { OneGraphApolloClientProvider } from '../../shared/apollo';
import apolloErrorLink from '../../shared/apollo/apolloErrorLink';
import { CrossPlatformRootTagProvider } from '../../shared/context/CrossPlatformRootTagContext';
import { normalizeEnvironment } from '../../shared/environment';
import { I18nProvider, useI18n, useLocale } from '../../shared/I18n';
import { BridgeProvider } from '../../shared/native-modules/BridgeContext';
import fetchNativeOneGraphOperation from '../../shared/native-modules/core/fetchNativeOneGraphQuery';
import {
    getCookiesForViewJob,
    type ViewJobReadableCookies,
} from '../../shared/native-modules/core/getCookies';
import { awaitBridges } from '../../shared/native-modules/tare/awaitBridges';
import { TareDataProvider } from '../../shared/native-modules/tare/TareDataContext';
import { parsePrforceGroups } from '../../shared/proctor/parsePrforceGroups';
import { buildProctorBucketAllocation } from '../../shared/proctor/ProctorBucketAllocation';
import { CookieProvider } from '../../shared/util/CookieProvider';
import { NavigationType } from '../../shared/util/NavigationType';
import ScreenType, {
    isWebBottomSheet,
    ScreenTypeProvider,
    useIsWebBottomSheet,
} from '../../shared/util/ScreenType';
import { ViewjobProctorProvider } from '../proctor/__generated__/ViewjobProctorContext';
import {
    RnViewjobOnegraphTst,
    ViewjobProctorSpecifications,
} from '../proctor/__generated__/ViewjobProctorSpecifications';

import { NavigationContainer, DefaultTheme, useNavigation } from '@react-navigation/native';
import {
    createNativeStackNavigator,
    type NativeStackHeaderProps,
    type NativeStackNavigationOptions,
} from '@react-navigation/native-stack';
import React, { Suspense, use, useMemo, useRef } from 'react';
import { Platform, StyleSheet } from 'react-native';

// default theme has a different background, override it to white
const CustomBackgroundTheme = {
    ...DefaultTheme,
    colors: {
        ...DefaultTheme.colors,
        background: 'white',
    },
};

const Stack = createNativeStackNavigator<RootStackParamList>();

function renderReportContentHeader(_props: NativeStackHeaderProps) {
    return <ReportContentHeader />;
}

/**
 * Provides a single Apollo client to every stack screen so mutations fired
 * from modal screens propagate to the parent screen's queries via Apollo's
 * normalized cache.
 */
function SharedApolloProvider({
    children,
    nativeContext,
    prforceGroups,
    url,
    apolloClient,
}: {
    children: React.ReactNode;
    nativeContext?: NativeContextRNProps;
    prforceGroups?: string | null;
    url?: string | null;
    apolloClient?: ApolloClient<NormalizedCacheObject>;
}) {
    const locale = useLocale();
    const errorLink = useMemo(
        () => apolloErrorLink({ provider: 'ViewJob', nativeContext }),
        [nativeContext],
    );
    const defaultOptions = useGetApolloDefaultOptions({ ctk: nativeContext?.ctk, locale });
    const platformOptions = useMemo(() => getPlatformOptions(), []);
    const placementId = useMemo(
        () => (url ? getPlacementIdFromUrl(url) ?? undefined : undefined),
        [url],
    );

    return (
        <OneGraphApolloClientProvider
            fetch={fetchNativeOneGraphOperation}
            defaultOptions={defaultOptions}
            platformOptions={platformOptions}
            prforceGroups={prforceGroups ?? undefined}
            errorLink={errorLink}
            placementId={placementId}
            typePolicies={jobCardDataLocalTypePolicies}
            externalClient={apolloClient}
        >
            {children}
        </OneGraphApolloClientProvider>
    );
}

/**
 * Shared header back button (mads chevron + localized "Back" a11y label) used by the
 * pushed/modal View Job screens, so the back affordance matches the rest of View Job
 * instead of the native-stack default (which leaks the previous route name).
 */
export function HeaderBackButton() {
    const gt = useI18n();
    const navigation = useNavigation();

    return (
        <Pressable
            sx={backButtonStyles.button}
            onPress={() => navigation.goBack()}
            accessible={true}
            accessibilityRole="button"
            accessibilityLabel={gt.gettext('Back')}
        >
            <ChevronLeft fill="black" size="md" />
        </Pressable>
    );
}

// Module-level so it's a stable reference (avoids react/no-unstable-nested-components
// and matches the renderReportContentHeader pattern used by the other screens).
function renderHeaderBackButton() {
    return <HeaderBackButton />;
}

const backButtonStyles = StyleSheet.create({
    button: {
        paddingHorizontal: 16,
        paddingVertical: 8,
        marginLeft: 4,
    },
});

const reportContentWrapperStyles = StyleSheet.create({
    mobileContainer: {
        flex: 1,
        backgroundColor: 'white',
        width: '100%',
        bottom: 0,
        top: 32,
        overflow: 'hidden',
        borderTopLeftRadius: 12,
        borderTopRightRadius: 12,
        paddingBottom: 16,
        // @ts-expect-error - position 'fixed' is valid on web (react-native-web)
        position: 'fixed',
    },
    modalContent: {
        backgroundColor: 'white',
        width: 600,
        borderRadius: 20,
        padding: 12,
    },
    mobileBackground: {
        backgroundColor: 'rgba(0,0,0,0.4)',
        width: '100%',
        height: '100%',
    },
    modalBackground: {
        backgroundColor: 'rgba(0,0,0,0.4)',
        position: 'absolute',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
    },
});

/**
 * Wraps ReportContentModal in an extra container when on web (e.g. for layout or styling).
 * On native, renders ReportContentModal directly.
 */
function ReportContentScreenWrapper({
    isDesktop = false,
    ...props
}: React.ComponentProps<typeof ReportContentModal> & { isDesktop?: boolean }) {
    const isWebBottomSheet = useIsWebBottomSheet();
    const reportContent = <ReportContentModal {...props} />;
    if (isWebBottomSheet) {
        return (
            <Box sx={reportContentWrapperStyles.mobileBackground}>
                <Box sx={reportContentWrapperStyles.mobileContainer}>
                    <ReportContentHeader />
                    {reportContent}
                </Box>
            </Box>
        );
    }
    if (isDesktop) {
        return (
            <Modal animationType="fade" visible={true} transparent={true}>
                <Box sx={reportContentWrapperStyles.modalBackground}>
                    <Box sx={reportContentWrapperStyles.modalContent}>
                        <ReportContentHeader />
                        {reportContent}
                    </Box>
                </Box>
            </Modal>
        );
    }
    return <>{reportContent}</>;
}

/**
 * Returns platform-specific navigation options for the CollectionModal screen.
 * iOS uses NavigationBar component, Android uses CollectionModalHeader with custom back button for accessibility.
 */
const getCollectionModalOptions = () => {
    if (Platform.OS === 'ios') {
        return () => ({
            headerShown: true,
            presentation: 'modal' as const,
            header: (props: NativeStackHeaderProps) => (
                <NavigationBar
                    url={''}
                    showSaveJobButton={false}
                    showShareButton={false}
                    isLoggedIn={false}
                    reload={() => {}}
                    title={(props.route.params as { title?: string })?.title ?? ''}
                    showBorder={true}
                    renderedOn="CollectionModal"
                />
            ),
        });
    }

    return (): NativeStackNavigationOptions => ({
        headerShown: true,
        presentation: 'modal' as const,
        headerTitle: () => <CollectionModalHeader />,
        headerLeft: renderHeaderBackButton,
    });
};

export type ViewJobBridgeDependencies = {
    RNTareBridge: TypedNativeModules['RNTareBridge'];
    RNCoreNativeModule: TypedNativeModules['RNCoreNativeModule'];
    ViewJobModule: TypedNativeModules['ViewJobModule'];
    proctorAllocations: Record<string, ProctorAllocation>;
    appModel: TareAppModel;
    accountStatus: TareAccountStatus;
    deviceModel: TareDeviceModel;
    appSessionInfo: JSTAppSessionInfo | null;
    viewjobCookies: Record<ViewJobReadableCookies, string | null>;
};

/**
 * The type definition for the entrypoint of the app.
 *
 * Partial marks every field as potentially undefined because
 * old app versions will not provide newly introduced fields
 *
 * We omit 'getVJData' because it is used as a prop to provide
 * mock data. The real implementation is provided through the
 * `NativeModules` integration.
 *
 * We add a locale prop because old versions of the app do not
 * have access to TARE.
 */
export type AppProps = Partial<
    Omit<AppContentProps, 'getVJData'> & {
        locale: string;
        navigationType?: NavigationType | null;
        /**
         * Host display surface. On the OneGraph path there is no preloaded
         * model to read `rnViewJobDisplay` from, so the host supplies it here.
         */
        viewJobDisplay?: RnViewJobDisplay;
        /**
         * Web only: the caller's explicit signal to use the OneGraph data path.
         * The OneGraph embed (`mountViewJobWithContext`) sets this; the legacy
         * `mountViewJob` embed and the standalone web entries leave it unset and
         * stay on REST. Ignored on native, which gates on rn_viewjob_onegraph_tst.
         */
        forceOneGraph?: boolean;
        // The Apollo SSR render entry resolves bridge dependencies before rendering
        // and passes the resolved value; the app/native paths pass a Promise.
        bridgeDependencies: Promise<ViewJobBridgeDependencies> | ViewJobBridgeDependencies;
        viewJobSource?: 'ViewJob' | 'ViewJobFeed';
        onRendered?: () => void;
        apolloClient?: ApolloClient<NormalizedCacheObject>;
    }
>;

export async function createViewJobBridgeDependencies(
    bridges: BridgeImplementations<'RNCoreNativeModule' | 'RNTareBridge' | 'ViewJobModule'>,
): Promise<ViewJobBridgeDependencies> {
    const [
        RNTareBridge,
        RNCoreNativeModule,
        ViewJobModule,
        appModel,
        accountStatus,
        deviceModel,
        appSessionInfo,
        proctorAllocations,
        viewjobCookies,
    ] = await Promise.all([
        bridges.RNTareBridge,
        bridges.RNCoreNativeModule,
        bridges.ViewJobModule,
        bridges.RNTareBridge.then((tareApi) => tareApi.getAppModel()),
        bridges.RNTareBridge.then((tareApi) => tareApi.getAccountStatus()),
        bridges.RNTareBridge.then((tareApi) => tareApi.getDeviceModel()),
        bridges.RNTareBridge.then((tareApi) => tareApi.getAppSessionInfo()),
        bridges.RNTareBridge.then((tareApi) =>
            tareApi.getProctorAllocations(ViewjobProctorSpecifications.map((t) => t.name)),
        ),
        bridges.RNCoreNativeModule.then((coreApi) => getCookiesForViewJob(coreApi)),
    ]);

    // The native bridge returns the raw `buildEnvironment` enum, which is
    // uppercase on Android (`PROD`/`QA`/`E2EQA`) and lowercase on iOS.
    // Canonicalize at the bridge boundary so consumers reading
    // `tareData.appModel?.buildEnvironment` see the same shape on both
    // platforms (RN-3079).
    const normalizedAppModel: TareAppModel = {
        ...appModel,
        buildEnvironment: normalizeEnvironment(appModel.buildEnvironment),
    };

    return {
        RNTareBridge,
        RNCoreNativeModule,
        ViewJobModule,
        appModel: normalizedAppModel,
        accountStatus,
        deviceModel,
        appSessionInfo,
        proctorAllocations,
        viewjobCookies,
    };
}

function App({
    url,
    trigger,
    source,
    nativeContext,
    loggingUrl,
    prforceGroups,
    renderStarts,
    navigationType,
    locale: localeProp,
    preloadedVJData,
    viewJobDisplay,
    forceOneGraph,
    bridgeDependencies,
    viewJobSource = 'ViewJob',
    onRendered,
    apolloClient,
}: AppProps) {
    // `bridgeDependencies` may be a Promise (app / native / client paths) or an
    // already-resolved value (the Apollo SSR render entry awaits it before rendering).
    // Unwrap only when it is actually a thenable so a resolved value isn't mis-`use`d.
    const deps =
        typeof (bridgeDependencies as { then?: unknown })?.then === 'function'
            ? use(bridgeDependencies as Promise<ViewJobBridgeDependencies>)
            : (bridgeDependencies as ViewJobBridgeDependencies);
    const {
        RNTareBridge: tareApi,
        RNCoreNativeModule: coreApi,
        ViewJobModule: viewjobApi,
        appModel,
        accountStatus,
        deviceModel,
        appSessionInfo,
        proctorAllocations,
        viewjobCookies,
    } = deps;

    const oneGraphAllocation = buildProctorBucketAllocation(
        RnViewjobOnegraphTst,
        proctorAllocations[RnViewjobOnegraphTst.name],
    );

    // This is part of the rendered output,
    // so it must evaluate identically on the server and the hydrating client. The server
    // receives the injected `apolloClient` (passed only by renderWithApolloState); the
    // browser receives the serialized `window.__APOLLO_STATE__` (injected only for
    // Apollo-SSR'd pages). Both are present iff this page came from the Apollo SSR entry,
    // so server and client agree and hydration matches. Otherwise, fall back to the
    // per-platform gating below.
    const isApolloDriven =
        apolloClient != null || (typeof window !== 'undefined' && window.__APOLLO_STATE__ != null);

    // On web the caller picks the data path and signals it explicitly via `forceOneGraph`
    // (the OneGraph embed `mountViewJobWithContext` sets it; the legacy `mountViewJob` and
    // standalone web entries leave it off and stay on REST). Native has a single entry and
    // no caller choice, so it gates on rn_viewjob_onegraph_tst (buckets 2 and 3).
    const enableOneGraph =
        Platform.OS === 'web'
            ? forceOneGraph === true
            : oneGraphAllocation.value === 2 || oneGraphAllocation.value === 3;

    const shouldCallOneGraph = isApolloDriven || enableOneGraph;

    // Resolve the host display surface once. Prefer the explicit prop (OneGraph
    // path); fall back to a preloaded model's value (REST / SSR path).
    const resolvedViewJobDisplay: RnViewJobDisplay =
        viewJobDisplay ??
        (preloadedVJData?.rnViewJobDisplay as RnViewJobDisplay | undefined) ??
        'MOBILE_STANDALONE';
    const isDesktopDisplay = isDesktopDisplaySurface(resolvedViewJobDisplay);
    const screenType =
        navigationType === NavigationType.PRESENT_SHEET
            ? isDesktopDisplay
                ? ScreenType.EMBEDDED_PANE
                : ScreenType.BOTTOM_SHEET
            : ScreenType.FULL_SCREEN;

    const locale = extractLocale(appModel, localeProp);

    const App = shouldCallOneGraph ? AppContentGraphQL : AppContent;

    return (
        <ViewJobDisplayProvider display={resolvedViewJobDisplay}>
            <CrossPlatformRootTagProvider>
                <BridgeProvider
                    implementations={{
                        RNTareBridge: tareApi,
                        RNCoreNativeModule: coreApi,
                        ViewJobModule: viewjobApi,
                    }}
                >
                    <TareDataProvider
                        tareData={{ appModel, deviceModel, accountStatus, appSessionInfo }}
                    >
                        <I18nProvider locale={locale}>
                            <ScreenTypeProvider screenType={screenType}>
                                <ViewjobProctorProvider
                                    allocations={proctorAllocations}
                                    override={parsePrforceGroups(
                                        nativeContext?.prforceGroups ?? '',
                                    )}
                                >
                                    <CookieProvider<ViewJobReadableCookies>
                                        cookies={viewjobCookies}
                                    >
                                        <SharedApolloProvider
                                            nativeContext={nativeContext}
                                            prforceGroups={prforceGroups}
                                            url={url}
                                            apolloClient={apolloClient}
                                        >
                                            <NavigationContainer
                                                onReady={onRendered}
                                                theme={CustomBackgroundTheme}
                                                documentTitle={
                                                    Platform.OS === 'web'
                                                        ? {
                                                              enabled: false,
                                                          }
                                                        : undefined
                                                }
                                            >
                                                <JMUIModalContextProvider>
                                                    <Stack.Navigator
                                                        screenOptions={{ headerShown: false }}
                                                    >
                                                        <Stack.Screen name="ViewJob">
                                                            {() => (
                                                                <App
                                                                    url={url ?? null}
                                                                    loggingUrl={loggingUrl ?? null}
                                                                    getVJData={
                                                                        viewjobApi.getVJData
                                                                    }
                                                                    prforceGroups={
                                                                        prforceGroups ?? null
                                                                    }
                                                                    trigger={trigger}
                                                                    source={source}
                                                                    nativeContext={nativeContext}
                                                                    renderStarts={renderStarts}
                                                                    preloadedVJData={
                                                                        preloadedVJData
                                                                    }
                                                                    viewJobSource={viewJobSource}
                                                                    apolloClient={apolloClient}
                                                                />
                                                            )}
                                                        </Stack.Screen>
                                                        <Stack.Screen
                                                            name="ReportContent"
                                                            options={() => ({
                                                                headerShown:
                                                                    !isWebBottomSheet(
                                                                        screenType,
                                                                    ) && !isDesktopDisplay,
                                                                presentation: isDesktopDisplay
                                                                    ? 'transparentModal'
                                                                    : 'modal',
                                                                header: renderReportContentHeader,
                                                            })}
                                                        >
                                                            {(props) => (
                                                                <ReportContentScreenWrapper
                                                                    isDesktop={isDesktopDisplay}
                                                                    {...props}
                                                                />
                                                            )}
                                                        </Stack.Screen>
                                                        <Stack.Screen
                                                            name="CollectionModal"
                                                            component={CollectionModal}
                                                            options={getCollectionModalOptions()}
                                                        />
                                                        <Stack.Screen
                                                            name="CommutePreferenceModal"
                                                            options={
                                                                isDesktopDisplay
                                                                    ? {
                                                                          headerShown: false,
                                                                          presentation:
                                                                              'transparentModal',
                                                                      }
                                                                    : {
                                                                          headerShown: false,
                                                                          presentation:
                                                                              'formSheet',
                                                                          sheetAllowedDetents: [
                                                                              0.6,
                                                                          ],
                                                                          sheetCornerRadius: 20,
                                                                          gestureEnabled: true,
                                                                      }
                                                            }
                                                        >
                                                            {() => (
                                                                <CommutePreferenceModal
                                                                    isDesktop={isDesktopDisplay}
                                                                />
                                                            )}
                                                        </Stack.Screen>
                                                        <Stack.Screen
                                                            name="AIDisclaimerModal"
                                                            component={AIDisclaimerModal}
                                                            options={{
                                                                headerShown: false,
                                                                presentation: 'formSheet',
                                                                sheetAllowedDetents: [0.225],
                                                                sheetCornerRadius: 16,
                                                                gestureEnabled: false,
                                                            }}
                                                        />
                                                        <Stack.Screen
                                                            name="CompanySummaryDisclaimerModal"
                                                            component={
                                                                CompanySummaryDisclaimerModal
                                                            }
                                                            options={{
                                                                headerShown: false,
                                                                presentation: 'formSheet',
                                                                sheetAllowedDetents: [0.225],
                                                                sheetCornerRadius: 16,
                                                                gestureEnabled: false,
                                                            }}
                                                        />
                                                        <Stack.Screen
                                                            name="ProfileWebView"
                                                            component={ProfileWebViewScreen}
                                                            options={{
                                                                headerShown: true,
                                                                // Match the back-button convention used by the
                                                                // other VJ header screens (mads chevron +
                                                                // localized "Back" label) instead of the
                                                                // native-stack default, which would show the
                                                                // previous route name ("ViewJob").
                                                                headerLeft: renderHeaderBackButton,
                                                            }}
                                                        />
                                                    </Stack.Navigator>
                                                </JMUIModalContextProvider>
                                            </NavigationContainer>
                                        </SharedApolloProvider>
                                    </CookieProvider>
                                </ViewjobProctorProvider>
                            </ScreenTypeProvider>
                        </I18nProvider>
                    </TareDataProvider>
                </BridgeProvider>
            </CrossPlatformRootTagProvider>
        </ViewJobDisplayProvider>
    );
}

/**
 * Entry point for ViewJob.
 * - RNW (React Native Web): bridgeDependencies is initialized by the parent in web/viewjob/index.server.ts
 * - RN (React Native): bridgeDependencies is created here on each mount to ensure fresh cookies
 */
export const AppWithSuspenseBoundary: React.FC<AppProps> = (props) => {
    const renderStarts = useRef(Date.now());
    const bridgeDependencies = useMemo(
        () =>
            props.bridgeDependencies ??
            createViewJobBridgeDependencies(
                awaitBridges(['RNTareBridge', 'RNCoreNativeModule', 'ViewJobModule']),
            ),
        [props.bridgeDependencies],
    );

    return (
        <Suspense>
            <App
                {...props}
                renderStarts={renderStarts.current}
                bridgeDependencies={bridgeDependencies}
            />
        </Suspense>
    );
};

export default AppWithSuspenseBoundary;
